/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 17-Jul-2026
 * @author      : Vincy R
 * Requirement# : 22300
 * Purpose      : Promote a tenant's Global-Default-based fork into the trade's first provider default.
 */

import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { Trade, Tenant } from '../types';

export interface PromoteModalProps {
  open: boolean; trade: Trade; tenant: Tenant; sourceVersion: string;
  onClose: () => void; onConfirm: () => void;
}

/**
 * Shown after a tenant saves customizations on a trade with no dedicated provider template
 * (i.e. their fork traces back to the Global Default). Offers to promote their version into
 * that trade's first Provider default so future tenants adopting it start here instead.
 */
export default function PromoteModal({ open, trade, tenant, sourceVersion, onClose, onConfirm }: PromoteModalProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Promote to {trade.name}'s provider default?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          This trade does not currently have a dedicated provider template — {tenant.name} is editing a copy of the <b>Global Default Template</b> ({sourceVersion}).
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Promoting creates {trade.name}'s first dedicated Provider default template from this customized version, so future tenants adopting this trade start here instead of the Global Default.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Not now</Button>
        <Button variant="contained" onClick={onConfirm}>Promote</Button>
      </DialogActions>
    </Dialog>
  );
}
